const COLLECTION_OVERVIEW_APPLICATIONS_PARAM = 'filter.p.m.custom.applications';
let collectionOverviewHistoryPatched = false;

// facets.js updates the URL via history.pushState without emitting an event,
// so re-dispatch history updates as a window event for tab syncing.
function patchHistoryForCollectionOverview() {
  if (collectionOverviewHistoryPatched) return;
  collectionOverviewHistoryPatched = true;

  ['pushState', 'replaceState'].forEach((method) => {
    const original = history[method].bind(history);
    history[method] = (...args) => {
      const result = original(...args);
      window.dispatchEvent(new CustomEvent('collection-overview:locationchange'));
      return result;
    };
  });
}

function getApplicationsFilterValues(urlString) {
  if (!urlString) return [];

  let url;
  try {
    url = new URL(urlString, window.location.origin);
  } catch (error) {
    return [];
  }

  return url.searchParams
    .getAll(COLLECTION_OVERVIEW_APPLICATIONS_PARAM)
    .map((value) => value.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function normalizeApplicationsFilterValue(value) {
  return value.replace(/\s+/g, ' ').trim().toLowerCase();
}

class CollectionOverviewTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tablist = this.querySelector('.collection-overview__tablist');

    if (!this.tabs.length) return;

    if (this.tablist) {
      this._orderedTabItems = [...this.tablist.querySelectorAll('.collection-overview__tab-item')];
    }

    this._onMobileLayoutChange = () => {
      this.applyMobileTabLayout();
      this.refreshTabMarquees();
    };

    window.addEventListener('resize', this._onMobileLayoutChange, { passive: true });

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.selectTab(tab));
      tab.addEventListener('keydown', (event) => this.onTabKeydown(event, tab));
    });

    const selectedTab = this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];
    this.selectTab(selectedTab);
    this.initUrlFilterSync();

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.refreshTabMarquees());
    }
  }

  disconnectedCallback() {
    if (this._onMobileLayoutChange) {
      window.removeEventListener('resize', this._onMobileLayoutChange);
    }

    if (this._onUrlFilterChange) {
      window.removeEventListener('popstate', this._onUrlFilterChange);
      window.removeEventListener('collection-overview:locationchange', this._onUrlFilterChange);
    }
  }

  initUrlFilterSync() {
    this._urlFilterTabs = this.tabs.filter(
      (tab) => getApplicationsFilterValues(tab.dataset.linkUrl).length === 1
    );
    if (!this._urlFilterTabs.length) return;

    this._defaultTab =
      this.tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || this.tabs[0];

    this._onUrlFilterChange = () => this.syncTabWithUrlFilter();
    window.addEventListener('popstate', this._onUrlFilterChange);
    window.addEventListener('collection-overview:locationchange', this._onUrlFilterChange);
    patchHistoryForCollectionOverview();

    this.syncTabWithUrlFilter();
  }

  syncTabWithUrlFilter() {
    const currentValues = getApplicationsFilterValues(window.location.href);
    const previousCount = this._lastUrlFilterCount;
    this._lastUrlFilterCount = currentValues.length;

    if (currentValues.length === 0) {
      // Reset to the default tab only when a URL update clears a single
      // selected application (not on initial unfiltered load, where
      // previousCount is undefined, and not when clearing multiple values).
      if (
        previousCount === 1 &&
        this._defaultTab &&
        this._defaultTab.getAttribute('aria-selected') !== 'true'
      ) {
        this.selectTab(this._defaultTab);
      }
      return;
    }

    if (currentValues.length !== 1) return;

    const target = normalizeApplicationsFilterValue(currentValues[0]);
    const matchingTab = this._urlFilterTabs.find((tab) => {
      const tabValues = getApplicationsFilterValues(tab.dataset.linkUrl);
      return tabValues.length === 1 && normalizeApplicationsFilterValue(tabValues[0]) === target;
    });

    if (!matchingTab || matchingTab.getAttribute('aria-selected') === 'true') return;

    this.selectTab(matchingTab);
  }

  isMarqueeTabsMode() {
    return Boolean(this.tablist);
  }

  restoreTabOrder() {
    if (!this.tablist || !this._orderedTabItems) return;

    this._orderedTabItems.forEach((item) => {
      this.tablist.appendChild(item);
    });
  }

  applyMobileTabLayout() {
    if (!this.tablist) return;

    this.restoreTabOrder();
  }

  tabTextOverflows(tab) {
    const track = tab.querySelector('.collection-overview__tab-text-track');
    if (!track) return false;

    return track.scrollWidth > track.clientWidth + 1;
  }

  clearTabMarquee(tab) {
    tab.classList.remove('collection-overview__tab--marquee');
    tab.style.removeProperty('--co-tab-marquee-duration');

    const track = tab.querySelector('.collection-overview__tab-text-track');
    if (!track) return;

    track.querySelectorAll('.collection-overview__tab-text-segment[aria-hidden="true"]').forEach((node) => {
      node.remove();
    });
  }

  refreshTabMarquees() {
    if (!this.isMarqueeTabsMode()) {
      this.tabs.forEach((tab) => this.clearTabMarquee(tab));
      return;
    }

    this.tabs.forEach((tab) => {
      const track = tab.querySelector('.collection-overview__tab-text-track');
      const segment = tab.querySelector('.collection-overview__tab-text-segment');

      this.clearTabMarquee(tab);

      const isSelected = tab.getAttribute('aria-selected') === 'true';
      if (!isSelected || !track || !segment || !this.tabTextOverflows(tab)) return;

      const singleWidth = track.scrollWidth;

      const duplicate = segment.cloneNode(true);
      duplicate.setAttribute('aria-hidden', 'true');
      track.appendChild(duplicate);

      tab.classList.add('collection-overview__tab--marquee');

      const duration = Math.max(10, Math.min(28, singleWidth / 22));
      tab.style.setProperty('--co-tab-marquee-duration', `${duration}s`);
    });
  }

  selectTab(selectedTab) {
    const panelId = selectedTab.getAttribute('aria-controls');
    const tabItem = selectedTab.closest('.collection-overview__tab-item');

    this.querySelectorAll('.collection-overview__tab-item').forEach((item) => {
      item.classList.toggle('is-selected', item === tabItem);
    });

    this.tabs.forEach((tab) => {
      const isSelected = tab === selectedTab;
      tab.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      tab.tabIndex = isSelected ? 0 : -1;
    });

    this.panels.forEach((panel) => {
      panel.hidden = panel.id !== panelId;
    });

    this.applyMobileTabLayout();

    window.requestAnimationFrame(() => {
      this.refreshTabMarquees();
      updateCollectionOverviewGridLinkTooltips(this);
      const panel = this.closest('.collection-overview__panel');
      if (panel) refreshCollectionOverviewScrollHint(panel);
    });
  }

  onTabKeydown(event, currentTab) {
    const index = this.tabs.indexOf(currentTab);
    let nextIndex = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (index + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
        nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = this.tabs[nextIndex];
    this.selectTab(nextTab);
    nextTab.focus();
  }
}

function isGridLinkLabelTruncated(label) {
  return label.scrollWidth > label.clientWidth + 1;
}

function updateCollectionOverviewGridLinkTooltips(root = document) {
  root.querySelectorAll('.collection-overview__grid-link-text').forEach((textWrap) => {
    const label = textWrap.querySelector('.collection-overview__grid-link-label');
    if (!label) return;

    textWrap.classList.toggle('is-truncated', isGridLinkLabelTruncated(label));
  });
}

function initCollectionOverviewInfoTooltips(root = document) {
  root.querySelectorAll('.collection-overview__header-icon-wrap:not([data-co-info-tooltip-init])').forEach((wrap) => {
    wrap.dataset.coInfoTooltipInit = 'true';

    const button = wrap.querySelector('.collection-overview__header-icon');
    if (!button) return;

    let outsideClickHandler = null;

    const setActive = (active) => {
      wrap.classList.toggle('is-active', active);
      button.setAttribute('aria-expanded', active ? 'true' : 'false');

      if (outsideClickHandler) {
        document.removeEventListener('click', outsideClickHandler);
        outsideClickHandler = null;
      }

      if (active) {
        outsideClickHandler = (event) => {
          if (!wrap.contains(event.target)) {
            setActive(false);
          }
        };
        document.addEventListener('click', outsideClickHandler);
      }
    };

    button.addEventListener('click', (event) => {
      event.stopPropagation();
      setActive(!wrap.classList.contains('is-active'));
    });

    button.addEventListener('blur', () => {
      window.setTimeout(() => {
        if (!wrap.contains(document.activeElement)) {
          setActive(false);
        }
      }, 0);
    });

    button.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setActive(false);
      }
    });
  });
}

function initCollectionOverviewGridLinkTooltips(root = document) {
  root.querySelectorAll('.collection-overview__grid-link-text:not([data-co-tooltip-init])').forEach((textWrap) => {
    textWrap.dataset.coTooltipInit = 'true';
    const label = textWrap.querySelector('.collection-overview__grid-link-label');
    if (!label) return;

    const updateTruncation = () => {
      textWrap.classList.toggle('is-truncated', isGridLinkLabelTruncated(label));
    };

    updateTruncation();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(updateTruncation);
      observer.observe(label);
      observer.observe(textWrap);
    } else {
      window.addEventListener('resize', updateTruncation);
    }
  });

  updateCollectionOverviewGridLinkTooltips(root);
}

function loadCollectionOverviewModal(modal) {
  const body = modal.querySelector('.collection-overview-modal__body');
  const url = modal.dataset.coUrl;

  if (!body || !url || body.dataset.loaded === 'true') return;

  fetch(url)
    .then((response) => response.text())
    .then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const mainContent = doc.getElementById('MainContent');

      if (mainContent) {
        body.innerHTML = mainContent.innerHTML;
        body.dataset.loaded = 'true';
      }
    })
    .catch((error) => {
      console.error('Collection overview modal error:', error);
    });
}

const SCROLL_HINT_THRESHOLD = 8;
const SCROLL_HINT_DURATION = 4000;

function isPanelBodyScrollable(panelBody) {
  return panelBody.scrollHeight > panelBody.clientHeight + SCROLL_HINT_THRESHOLD;
}

function isPanelBodyAtBottom(panelBody) {
  return panelBody.scrollTop + panelBody.clientHeight >= panelBody.scrollHeight - SCROLL_HINT_THRESHOLD;
}

function hideCollectionOverviewScrollHint(panel) {
  const hint = panel.querySelector('.collection-overview__scroll-hint');
  if (!hint) return;

  clearTimeout(hint._hideTimer);
  hint.classList.remove('is-visible');
  hint.classList.add('is-hidden');
}

function showCollectionOverviewScrollHint(panel) {
  const panelBody = panel.querySelector('.collection-overview__panel-body');
  const hint = panel.querySelector('.collection-overview__scroll-hint');
  if (!panelBody || !hint) return;

  if (!isPanelBodyScrollable(panelBody) || isPanelBodyAtBottom(panelBody)) {
    hideCollectionOverviewScrollHint(panel);
    return;
  }

  clearTimeout(hint._hideTimer);
  hint.classList.remove('is-hidden');
  hint.classList.add('is-visible');

  hint._hideTimer = setTimeout(() => {
    hideCollectionOverviewScrollHint(panel);
  }, SCROLL_HINT_DURATION);
}

function refreshCollectionOverviewScrollHint(panel) {
  const hint = panel.querySelector('.collection-overview__scroll-hint');
  if (!hint) return;

  clearTimeout(hint._hideTimer);
  hint.classList.remove('is-visible', 'is-hidden');

  window.requestAnimationFrame(() => {
    showCollectionOverviewScrollHint(panel);
  });
}

function initCollectionOverviewScrollHint(panel) {
  if (panel.dataset.coScrollHintInit) return;
  panel.dataset.coScrollHintInit = 'true';

  const panelBody = panel.querySelector('.collection-overview__panel-body');
  if (!panelBody) return;

  panelBody.addEventListener(
    'scroll',
    () => {
      hideCollectionOverviewScrollHint(panel);
    },
    { passive: true }
  );

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        const hint = panel.querySelector('.collection-overview__scroll-hint');
        if (!hint || hint.classList.contains('is-visible')) return;

        if (isPanelBodyScrollable(panelBody) && !isPanelBodyAtBottom(panelBody)) {
          showCollectionOverviewScrollHint(panel);
        }
      });
    });
    observer.observe(panelBody);
  }

  window.requestAnimationFrame(() => {
    showCollectionOverviewScrollHint(panel);
  });
}

function initCollectionOverviewScrollHints(root = document) {
  root.querySelectorAll('.collection-overview__panel').forEach((panel) => {
    initCollectionOverviewScrollHint(panel);
  });
}

function initCollectionOverviewBackLinkPosition(backLink) {
  const header = document.querySelector('.section-header');
  const stickyHeader = document.querySelector('sticky-header');

  if (!header) return;

  const headerIsAlwaysSticky =
    stickyHeader?.getAttribute('data-sticky-type') === 'always' ||
    stickyHeader?.getAttribute('data-sticky-type') === 'reduce-logo-size';

  const updatePosition = () => {
    if (headerIsAlwaysSticky) {
      backLink.classList.add('is-header-visible');
      return;
    }

    const headerHidden = header.classList.contains('shopify-section-header-hidden');
    backLink.classList.toggle('is-header-visible', !headerHidden);
  };

  updatePosition();
  window.addEventListener('scroll', updatePosition, { passive: true });

  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(updatePosition);
    observer.observe(header, { attributes: true, attributeFilter: ['class'] });
  }
}

function initCollectionOverviewBackLink(root = document) {
  const overview = root.getElementById('CollectionOverview');
  const backLink = overview?.querySelector('.collection-overview__back-link');
  if (!overview || !backLink || backLink.dataset.coBackLinkInit) return;

  backLink.dataset.coBackLinkInit = 'true';
  backLink.removeAttribute('hidden');
  initCollectionOverviewBackLinkPosition(backLink);

  const setVisible = (visible) => {
    backLink.classList.toggle('is-visible', visible);
    backLink.setAttribute('aria-expanded', visible ? 'true' : 'false');
  };

  const updateVisibility = (entry) => {
    const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    setVisible(scrolledPast);
  };

  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver(
      ([entry]) => {
        updateVisibility(entry);
      },
      { threshold: 0 }
    );

    observer.observe(overview);
  } else {
    const onScroll = () => {
      const rect = overview.getBoundingClientRect();
      setVisible(rect.bottom < 0);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  backLink.addEventListener('click', () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    overview.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'start',
    });

    setVisible(false);
  });
}

function initCollectionOverviewModals(root = document) {
  root.querySelectorAll('.collection-overview-modal[data-co-url]').forEach((modal) => {
    if (modal.dataset.coInit) return;
    modal.dataset.coInit = 'true';

    const url = modal.dataset.coUrl;
    if (!url) return;

    document.querySelectorAll(`[data-modal="#${modal.id}"]`).forEach((opener) => {
      const button = opener.querySelector('button');
      if (!button) return;

      button.addEventListener('click', () => loadCollectionOverviewModal(modal));
    });
  });
}

if (!customElements.get('collection-overview-tabs')) {
  customElements.define('collection-overview-tabs', CollectionOverviewTabs);
}

document.addEventListener('DOMContentLoaded', () => {
  initCollectionOverviewModals();
  initCollectionOverviewInfoTooltips();
  initCollectionOverviewGridLinkTooltips();
  initCollectionOverviewScrollHints();
  initCollectionOverviewBackLink();
});

window.addEventListener('load', () => {
  updateCollectionOverviewGridLinkTooltips();
  document.querySelectorAll('.collection-overview__panel').forEach((panel) => {
    refreshCollectionOverviewScrollHint(panel);
  });
});
